import type { FC } from 'react';
import type React from 'react';
import { useEffect, useState } from 'react';
import includes from 'lodash/includes';

// utils
import { getFeatureFlags, getMessageCount } from './api';
import AppStoreModule from './AppStoreModule';

// components
import MenuIcon from './components/MenuIcon';
import Logo from './components/Logo';
import SectionRight from './components/SectionRight';
import MobileListView from './components/MobileListView';
import SectionLeft from './components/SectionLeft';
import AdminNav from './components/AdminNav';
import MockUserNav from './components/MockUserNav';
import SubNav from './components/SubNav';

// constellation
import { ThemeProvider } from 'styled-components';
import { ThemeConstellation } from '@zillow/constellation';
import { ResponsiveContainer } from '@zg-rentals/zrs-comet';

// styled components
import { StyledFlex, StyledHeaderNavigationWrapper } from './style';

// constants
import { HeaderTitles } from './constants';
import { PathsManifest } from '@zg-rentals/paths-manifest';

// interfaces
import type { Brand } from './interfaces/brand';
import type { Displays } from './interfaces/Displays';
import type { HeaderSubNavLink } from './interfaces/HeaderSubNavLink';
import type { User } from './interfaces/user';

export type Props = Displays & {
  brand: Brand;
  /*
   * userModel
   *
   * the user model
   */
  userModel?: User;
  /*
   * pathname
   *
   * the current path so the link can be highlighted
   */
  pathname: string;
  /*
   * proxyPath
   *
   * required for notificationcenter to make requests
   */
  proxyPath?: string;
  /*
   * csrf_token
   *
   * required for notificationcenter to make requests(put patch delete)
   */
  csrf_token?: string;
  /*
   * maxWidth
   *
   * Sets the maximum width of the container.
   */
  maxWidth?: string;
  /*
   * The current host serving the page, defaults to production if not provided
   *
   */
  host?: string;
  /*
   * The current ZG API being used to serve the page. Used for impersonation nav
   */
  zgApi?: string;
  /*
   * useRelativePaths
   *
   * Set to true if you want the paths to be relative.
   * Must pass reactRouterLink if set to true.
   */
  useRelativePaths?: boolean;
  /**
   * Overrides the path used when user clicks on the logo.
   */
  logoPathOverride?: string;
  /*
   * for navigating away from client side router
   */
  escapeAllLinks?: boolean;

  /*
   * reactRouterLink
   *
   * pass react-router-dom Link
   * ex: import { Link as reactRouterLink } from 'react-router-dom';
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  reactRouterLink: React.ComponentType<React.PropsWithChildren<any>>;

  /*
   * handleLogoutClick
   *
   * handle logout click
   */
  handleLogoutClick?(event?: Event): Promise<void> | void;

  /*
   * Handle any work required to be done before stopping impersonating
   */
  handleStopImpersonatingClick?(event?: Event): Promise<void> | void;

  /*
   * onLoginClick
   *
   * handle login click
   */
  onLoginClick?(event?: Event): Promise<void> | void;

  /*
   * webSocketInstance
   * Handle webSocket connection provided by consuming app
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  webSocketInstance?: any;
  listingId?: string;
  stickyHeaderNavZRMHomePage?: boolean;
};

interface AppConfig {
  features: {
    displayPrideLogo: boolean;
  };
}

const HeaderNavigation: FC<React.PropsWithChildren<Props>> = (props: Props) => {
  const [appConfig, setAppConfig] = useState<AppConfig>({
    features: {
      displayPrideLogo: false,
    },
  });
  const [isMenuExpanded, setMenuExpanded] = useState(false);
  const [unreadCountDisplay, setUnreadCountDisplay] = useState('');
  const [showUnreadDot, setShowUnreadDot] = useState(false);

  const useAbsolutePaths = !props.useRelativePaths || !props.reactRouterLink;
  const paths = new PathsManifest('/rental-manager', props.brand, useAbsolutePaths, props.host).getAllPaths();

  const {
    altLogo = true,
    brand,
    csrf_token = '',
    displayAccessMainContactSupport,
    displayAnalytics,
    displayBilling,
    displayMFHResources,
    displayMainAccountSettings,
    displayMainBilling,
    displayMainHelp,
    displayMainLeads,
    displayMainMaintenanceTracker,
    displayMainMessages,
    displayMainMyProperties,
    displayMainNotificationCenter,
    displayMainResources,
    displayMainSecuritySettings,
    displayUserManagement,
    listingId,
    maxWidth = '1232px',
    onLoginClick,
    pathname,
    proxyPath = '',
    reactRouterLink: v4Link,
    stickyHeaderNavZRMHomePage = false,
    userModel = {
      features: {},
    } as User,
    webSocketInstance = null,
    escapeAllLinks = false,
    zgApi = '',
  } = props;

  const { isEnterpriseCustomer = false, isMocked } = userModel ?? { isEnterpriseCustomer: false, isMocked: false };
  const {
    features: { displayPrideLogo },
  } = appConfig;

  const {
    canAccessBilling,
    canAccessMainBilling,
    canAccessMainAccountSettings,
    canAccessMainAnalytics,
    canAccessMainContactSupport,
    canAccessMainContacts,
    canAccessMainHelp,
    canAccessMainMessages,
    canAccessMainMyProperties,
    canAccessMainNotificationCenter,
    canAccessMainPayments,
    canAccessMainResources,
    canAccessMainSecuritySettings,
    canAccessMainMaintenanceTracker: aCanAccessMainMaintenanceTracker,
    canAccessMultiFamilyResources,
    canAccessUserManagement,
  } = userModel.features;

  // Temporary - waiting for confirmation to release enterprise help page
  // const helpPage = isEnterpriseCustomer ? paths.enterpriseHelpPage : paths.helpPage;
  const helpPage = paths.helpPage;

  // Pass link component from either react-router or next-js
  const Link = v4Link;

  const { base: basePage, signInPage: signInPageFunc } = paths;
  const signInPage = signInPageFunc(pathname) || '';

  const canAccessMainMaintenanceTracker = aCanAccessMainMaintenanceTracker && isEnterpriseCustomer === false;

  const setAppStoreModule = ({
    updatedAppConfig,
    updatedProxyPath,
  }: {
    updatedAppConfig: AppConfig;
    updatedProxyPath: string;
  }) => {
    new AppStoreModule().init({
      // @ts-ignore
      appConfig: updatedAppConfig,
      proxyPath: updatedProxyPath,
    });
  };

  useEffect(() => {
    const { features } = userModel ?? { features: {} };

    getFeatureFlags(proxyPath, csrf_token, zgApi)
      .then((res) => {
        const {
          appConfig: { config },
        } = res;

        // @ts-ignore FIXME needs better typing
        setAppConfig(config as AppConfig);
        setAppStoreModule({ updatedAppConfig: appConfig, updatedProxyPath: proxyPath });

        // @ts-ignore FIXME needs better typing
        const shouldFetchUnreadCount = config.features?.displayMessagesCount && features.canAccessMainMessages;

        // get the query param from the url
        const urlParams = new URLSearchParams(window.location.search);
        const showMessageCountInGleamParams = urlParams.get('showMessageCountInGleam');

        const shouldShowMessageCountInGleam =
          // @ts-ignore FIXME needs better typing
          config.features?.showMessageCountInGleam || showMessageCountInGleamParams === 'true';

        const showShowUnreadMessageGleamParams = urlParams.get('showUnreadMessageGleam');

        const shouldShowUnreadMessageGleam =
          // @ts-ignore FIXME needs better typing
          config.features?.showUnreadMessageGleam || showShowUnreadMessageGleamParams === 'true';

        if (shouldFetchUnreadCount) {
          return getMessageCount(proxyPath, csrf_token, zgApi).then((data) => {
            const {
              response: { unreadCount },
            } = data;

            let unreadConversationCountDisplay = '';

            if (unreadCount) {
              const isConversationCount100Plus = unreadCount >= 100;
              const addOnText = isConversationCount100Plus ? '+' : '';

              unreadConversationCountDisplay = isConversationCount100Plus
                ? '99'
                : shouldShowMessageCountInGleam
                  ? unreadCount.toString()
                  : '';
              setShowUnreadDot(!isConversationCount100Plus && shouldShowUnreadMessageGleam);
              setUnreadCountDisplay(`${unreadConversationCountDisplay}${addOnText}`);
            } else {
              setShowUnreadDot(false);
            }
            return;
          });
        }
        return;
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('HeaderNavigation#componentDidMount', err);
      });
    // eslint-disable-next-line
  }, []);

  const pathnameWithoutSearch = pathname.split('?')[0];
  const isZrmHomepage = pathnameWithoutSearch === '/rental-manager/' || pathnameWithoutSearch === '/rental-manager'; // internal redirects may not apply trailing slash
  const isPriceMyRentalPage = includes(pathnameWithoutSearch, 'price-my-rental');
  const isMarketTrendsPage = includes(pathnameWithoutSearch, 'market-trends');
  const isPostAListingMarketingPage = includes(pathnameWithoutSearch, 'post-a-listing');
  const shouldShowHeaderSubNav =
    isPriceMyRentalPage || isPostAListingMarketingPage || isZrmHomepage || isMarketTrendsPage;
  const headerSubNavLinks: Array<HeaderSubNavLink> = [
    {
      title: 'Zillow Rental Manager',
      url: paths.zrmHomePage,
      isActive: isZrmHomepage || isPostAListingMarketingPage,
      subTitles: [
        {
          title: 'Rental Listings',
          url: paths.postAListingMarketingPage,
          isActive: isPostAListingMarketingPage,
        },
        {
          title: 'Tenant Screening',
          url: paths.tenantScreeningStargatePage,
        },
        {
          title: 'Online Leases',
          url: paths.onlineLeasesPage,
        },
        {
          title: 'Rent Payments',
          url: paths.rentPaymentsPage,
        },
      ],
    },
    {
      title: 'Resources',
      url: paths.resourcesPage,
      isActive: isMarketTrendsPage,
      subTitles: [
        {
          title: 'Landlording Basics',
          url: paths.landlordingBasicsPage,
        },
        {
          title: 'Tools & Forms',
          url: paths.toolsAndFormsPage,
        },
        {
          title: 'Managing your Rental',
          url: paths.managingYourRentalPage,
        },
        {
          title: 'Landlord Laws & Regulations',
          url: paths.landlordLawsAndRegulationsPage,
        },
        {
          title: 'Market Trends',
          url: paths.zillowMarketTrendsPage,
          isActive: isMarketTrendsPage,
        },
      ],
    },
    {
      title: 'Price My Rental',
      url: paths.zillowPriceRentalLandingPage,
      isActive: isPriceMyRentalPage,
    },
    {
      title: HeaderTitles.postAListing,
      url: paths.propertiesPage + '?postingPath=true',
    },
  ];

  // use userModel.features to set display values or override via display props
  const displays = {
    altLogo,
    displayAccessMainContactSupport: canAccessMainContactSupport || displayAccessMainContactSupport,
    displayAdManagement: userModel?.isSelfServeCustomer || false,
    displayAnalytics: canAccessMainAnalytics || displayAnalytics,
    displayBilling: canAccessBilling || displayBilling,
    displayMFHResources: canAccessMultiFamilyResources || displayMFHResources,
    displayMainAccountSettings: canAccessMainAccountSettings || displayMainAccountSettings,
    displayMainBilling: canAccessMainBilling || displayMainBilling,
    displayMainHelp: canAccessMainHelp || displayMainHelp,
    displayMainLeads: canAccessMainContacts || displayMainLeads,
    displayMainMaintenanceTracker: canAccessMainMaintenanceTracker || displayMainMaintenanceTracker,
    displayMainMessages: canAccessMainMessages || displayMainMessages,
    displayMainMyProperties: canAccessMainMyProperties || displayMainMyProperties,
    displayMainNotificationCenter: canAccessMainNotificationCenter || displayMainNotificationCenter,
    displayMainResources: canAccessMainResources || displayMainResources,
    displayMainSecuritySettings: canAccessMainSecuritySettings || displayMainSecuritySettings,
    displayUserManagement: canAccessUserManagement || displayUserManagement,
    displayMainPayments: canAccessMainPayments,
  };

  const handleStopImpersonatingClick = () => {
    // fill in here
  };

  const logoLinkPath = props.logoPathOverride || `${basePage}/`;

  return (
    <ThemeProvider theme={ThemeConstellation}>
      <StyledHeaderNavigationWrapper data-testid="HeaderNavigationWrapper">
        <div data-testid="AdminNavContainer">
          {isMocked && <MockUserNav brand={brand} userModel={userModel} />}
          <AdminNav
            brand={brand}
            paths={paths}
            signInPage={signInPage}
            userModel={userModel}
            handleStopImpersonatingClick={handleStopImpersonatingClick}
            listingId={listingId}
          />
        </div>
        {/* @ts-ignore fix underlying types */}
        <ResponsiveContainer className="HeaderNavigation-container" maxWidth={maxWidth}>
          <StyledFlex justifyContent="space-between" alignItems="center" display="flex">
            <div className="HeaderNavigation-sectionLeft">
              <div className="HeaderNavigation-sectionLeft-mobile">
                <MenuIcon
                  isExpanded={isMenuExpanded}
                  handleToggle={setMenuExpanded}
                  data-testid="HeaderNavigation-sectionLeft-mobile-MenuIcon"
                />
              </div>
              <div className="HeaderNavigation-sectionLeft-desktop" data-testid="HeaderNavigation-sectionLeft-desktop">
                <SectionLeft
                  displays={displays}
                  paths={paths}
                  pathname={pathname}
                  userModel={userModel}
                  unreadCountDisplay={unreadCountDisplay}
                  useAbsolutePaths={useAbsolutePaths}
                  link={Link}
                  escapeAllLinks={escapeAllLinks}
                  showUnreadDot={showUnreadDot}
                />
              </div>
            </div>
            <div className="HeaderNavigation-sectionMiddle">
              <div
                className="HeaderNavigation-sectionMiddle-mobile"
                data-testid="HeaderNavigation-sectionMiddle-mobile"
              >
                <Logo isMobile={true} to={logoLinkPath} altLogo={altLogo} displayPrideLogo={displayPrideLogo} />
              </div>
              <div
                className="HeaderNavigation-sectionMiddle-desktop"
                data-testid="HeaderNavigation-sectionMiddle-desktop"
              >
                <Logo isMobile={false} to={logoLinkPath} altLogo={altLogo} displayPrideLogo={displayPrideLogo} />
              </div>
            </div>
            <div className="HeaderNavigation-sectionRight" data-testid="HeaderNavigation-sectionRight">
              <SectionRight
                brand={brand}
                csrf_token={csrf_token}
                displays={displays}
                paths={paths}
                helpPage={helpPage}
                pathname={pathname}
                proxyPath={proxyPath}
                userModel={userModel}
                onLoginClick={onLoginClick}
                webSocketInstance={webSocketInstance}
                zgApi={zgApi}
              />
            </div>
            {isMenuExpanded && (
              <MobileListView
                data-testid="HeaderNavigation-sectionRight-MobileListView"
                displays={displays}
                paths={paths}
                pathname={pathname}
                userModel={userModel}
                onLoginClick={onLoginClick}
                headerSubNavLinks={headerSubNavLinks}
                shouldShowHeaderSubNav={shouldShowHeaderSubNav}
                isZrmHomepage={isZrmHomepage}
              />
            )}
          </StyledFlex>
        </ResponsiveContainer>
      </StyledHeaderNavigationWrapper>
      {/* HeaderSubNav is rendered for desktop screen size only */}
      {shouldShowHeaderSubNav && (
        <SubNav
          headerSubNavLinks={headerSubNavLinks}
          isStickyHeaderNavActive={stickyHeaderNavZRMHomePage}
          isZrmHomepage={isZrmHomepage}
        />
      )}
    </ThemeProvider>
  );
};

export default HeaderNavigation;
